import React, { useEffect, useState } from 'react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import CartDrawer from '../components/cart/CartDrawer';
import { CONTACT_INFO, siteImages } from '../data/mock';
import { Phone, Mail, MapPin, Clock, Send, Cpu, Building2 } from 'lucide-react';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    document.title = 'Contact Bright Tech Supply | Customer Support';
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    // Simulate form submission
    await new Promise(resolve => setTimeout(resolve, 1000));
    setIsSubmitting(false);
    setSubmitted(true);
    setFormData({ name: '', email: '', subject: '', message: '' });
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <CartDrawer />
      
      <main className="">
        {/* Banner Section */}
        <section className="relative h-64 md:h-80 overflow-hidden">
          <div className="absolute inset-0">
            <img 
              src={siteImages.contactBanner}
              alt="Contact Us"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-white via-white/85 to-white/40" />
          </div>
          <div className="relative h-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-athletic-50 border border-athletic-200 rounded-full text-athletic-700 text-xs font-semibold mb-4">
                <Cpu className="w-3.5 h-3.5" />
                GET IN TOUCH
              </div>
              <h1 className="font-display text-4xl md:text-5xl text-gray-900 font-extrabold mb-3">
                Contact Us
              </h1>
              <p className="text-gray-600 max-w-lg">
                Have questions? Our team is here to help with your gadgets, orders and everything tech
              </p>
            </div>
          </div>
        </section>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="grid lg:grid-cols-3 gap-8">
            
            {/* Contact Info Cards */}
            <div className="lg:col-span-1 space-y-6">
              {/* Phone */}
              <a 
                href={`tel:${CONTACT_INFO.phone.replace(/[^0-9]/g, '')}`}
                className="block bg-gray-50 border border-gray-200 rounded-2xl p-6 hover:border-athletic-500/50 transition-all group"
              >
                <div className="w-14 h-14 bg-athletic-50 rounded-xl flex items-center justify-center mb-4 group-hover:bg-athletic-500/20 transition-colors">
                  <Phone className="w-6 h-6 text-athletic-700" />
                </div>
                <h3 className="font-display font-bold text-gray-900 text-lg mb-1">Phone</h3>
                <p className="text-athletic-700 font-semibold">{CONTACT_INFO.phone}</p>
                <p className="text-gray-500 text-sm mt-2">Mon-Fri 9AM-5PM EST</p>
              </a>

              {/* Email */}
              <a 
                href={`mailto:${CONTACT_INFO.email}`}
                className="block bg-gray-50 border border-gray-200 rounded-2xl p-6 hover:border-athletic-500/50 transition-all group"
              >
                <div className="w-14 h-14 bg-athletic-50 rounded-xl flex items-center justify-center mb-4 group-hover:bg-athletic-500/20 transition-colors">
                  <Mail className="w-6 h-6 text-athletic-700" />
                </div>
                <h3 className="font-display font-bold text-gray-900 text-lg mb-1">Email</h3>
                <p className="text-athletic-700 font-semibold">{CONTACT_INFO.email}</p>
                <p className="text-gray-500 text-sm mt-2">We'll respond within 24 hours</p>
              </a>

              {/* Address */}
              <div className="bg-gray-50 border border-gray-200 rounded-2xl p-6">
                <div className="w-14 h-14 bg-athletic-50 rounded-xl flex items-center justify-center mb-4">
                  <MapPin className="w-6 h-6 text-athletic-700" />
                </div>
                <h3 className="font-display font-bold text-gray-900 text-lg mb-1">Address</h3>
                <p className="text-gray-600 leading-relaxed">
                  {CONTACT_INFO.address.street}<br />
                  {CONTACT_INFO.address.city}, {CONTACT_INFO.address.state} {CONTACT_INFO.address.zip}<br />
                  {CONTACT_INFO.address.country}
                </p>
              </div>

              {/* Corporate Name Card */}
              <div className="bg-gradient-to-br from-athletic-500/10 to-athletic-600/10 border border-athletic-100 rounded-2xl p-6">
                <div className="w-14 h-14 bg-athletic-500/20 rounded-xl flex items-center justify-center mb-4">
                  <Building2 className="w-6 h-6 text-athletic-700" />
                </div>
                <h3 className="font-display font-bold text-gray-900 text-lg mb-1">Company</h3>
                <p className="text-athletic-700 font-semibold text-lg">{CONTACT_INFO.corpName}</p>
              </div>

              {/* Business Hours */}
              <div className="bg-gray-50 border border-gray-200 rounded-2xl p-6">
                <div className="w-14 h-14 bg-athletic-50 rounded-xl flex items-center justify-center mb-4">
                  <Clock className="w-6 h-6 text-athletic-700" />
                </div>
                <h3 className="font-display font-bold text-gray-900 text-lg mb-3">Business Hours</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Monday - Friday</span>
                    <span className="text-gray-900 font-medium">9:00 AM - 5:00 PM</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Saturday</span>
                    <span className="text-gray-900 font-medium">10:00 AM - 2:00 PM</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Sunday</span>
                    <span className="text-gray-500 font-medium">Closed</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <div className="bg-gray-50 border border-gray-200 rounded-2xl p-8">
                <h2 className="font-display text-2xl font-bold text-gray-900 mb-2">Send Us a Message</h2>
                <p className="text-gray-500 mb-8">Fill out the form below and we'll get back to you as soon as possible.</p>
                
                {submitted ? (
                  <div className="text-center py-12">
                    <div className="w-20 h-20 bg-athletic-50 rounded-full flex items-center justify-center mx-auto mb-6">
                      <Send className="w-10 h-10 text-athletic-700" />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">Message Sent!</h3>
                    <p className="text-gray-500 mb-6">We'll get back to you within 24 hours.</p>
                    <button
                      onClick={() => setSubmitted(false)}
                      className="px-6 py-3 bg-athletic-500 text-white font-semibold rounded-xl hover:bg-athletic-400 transition-colors"
                    >
                      Send Another Message
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-semibold text-gray-900 mb-2">Your Name</label>
                        <input
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-3 bg-white border border-dark-200 rounded-xl text-gray-900 placeholder-dark-400 focus:outline-none focus:border-athletic-500 transition-colors"
                          placeholder="John Doe"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-900 mb-2">Email Address</label>
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-3 bg-white border border-dark-200 rounded-xl text-gray-900 placeholder-dark-400 focus:outline-none focus:border-athletic-500 transition-colors"
                          placeholder="john@example.com"
                        />
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-semibold text-gray-900 mb-2">Subject</label>
                      <select
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 bg-white border border-dark-200 rounded-xl text-gray-900 focus:outline-none focus:border-athletic-500 transition-colors cursor-pointer"
                      >
                        <option value="">Select a subject</option>
                        <option value="order">Order Inquiry</option>
                        <option value="product">Product Question</option>
                        <option value="shipping">Shipping Information</option>
                        <option value="returns">Returns & Refunds</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-semibold text-gray-900 mb-2">Message</label>
                      <textarea
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        rows={6}
                        className="w-full px-4 py-3 bg-white border border-dark-200 rounded-xl text-gray-900 placeholder-dark-400 focus:outline-none focus:border-athletic-500 transition-colors resize-none"
                        placeholder="How can we help you?"
                      />
                    </div>
                    
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full flex items-center justify-center gap-2 px-8 py-4 bg-gradient-to-r from-athletic-500 to-athletic-600 hover:from-athletic-400 hover:to-athletic-500 text-white font-bold uppercase tracking-wider rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSubmitting ? (
                        <>
                          <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                          Sending...
                        </>
                      ) : (
                        <>
                          <Send className="w-5 h-5" />
                          Send Message
                        </>
                      )}
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default ContactPage;
